import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chat as chatApi, bookings as bookingsApi, pricing as pricingApi, hotels as hotelsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mic, MicOff, Send, Sparkles, ShieldCheck, MapPin, Users, Calendar, Lock, CreditCard, Phone, ArrowLeft, Receipt, Wallet, ExternalLink, AlertTriangle, CheckCircle, ShoppingCart, Zap } from 'lucide-react';
import { CryptoPayment } from '@/components/CryptoPayment';
import { GaslessCheckout } from '@/components/GaslessCheckout';
import { RoomPhotoGallery } from '@/components/booking/RoomPhotoGallery';
import { IdVerifyGate } from '@/components/booking/IdVerifyGate';
import { useCart } from '@/contexts/CartContext';
import { CartIconButton, CartDrawer, useCartDrawer } from '@/features/cart';
import { PreferenceCollectionModal } from '@/components/personalization/PreferenceCollectionModal';
import { ReliabilityBadge } from '@/components/reliability/ReliabilityBadge';
import { CancellationPolicyCard } from '@/components/reliability/CancellationPolicyCard';
import { CancellationBreakdown } from '@/components/cancellation/CancellationBreakdown';
import { RefundScenarios } from '@/components/cancellation/RefundScenarios';
import { toast } from 'sonner';
import '@/App.css';
import { BACKEND_URL } from '@/lib/backendUrl';

const genSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getExplorerLink = (chain, txHash) => {
  const explorers = {
    'ethereum': 'https://etherscan.io/tx/',
    'base': 'https://basescan.org/tx/',
    'polygon': 'https://polygonscan.com/tx/',
    'bitcoin': 'https://blockchair.com/bitcoin/transaction/',
    'solana': 'https://solscan.io/tx/'
  };
  return `${explorers[chain] || explorers['ethereum']}${txHash}`;
};

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 message-enter" data-testid="booking-chat-typing">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4 text-primary" />
      </div>
      <div className="bg-card/80 border border-border rounded-2xl px-4 py-3 shadow-sm">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 typing-dot" />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 typing-dot" />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 typing-dot" />
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ role, content, timestamp, isPersonalized }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-start gap-3 message-enter ${isUser ? 'flex-row-reverse' : ''}`}
         data-testid={isUser ? 'booking-chat-user-message' : 'booking-chat-assistant-message'}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
      )}
      <div className={`max-w-[80%] ${isUser
        ? 'bg-primary text-primary-foreground rounded-2xl px-4 py-3 shadow-sm'
        : 'bg-card/80 border border-border rounded-2xl px-4 py-3 shadow-sm relative before:absolute before:left-0 before:top-3 before:w-[3px] before:h-6 before:bg-teal-400/70 before:rounded-full'}`}>
        {!isUser && isPersonalized && (
          <Badge 
            variant="secondary" 
            className="mb-2 text-[9px] bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 border-violet-200"
            data-testid="personalized-badge"
          >
            <Sparkles className="w-2.5 h-2.5 mr-1" />
            Personalized for You
          </Badge>
        )}
        <p className="text-sm md:text-base leading-relaxed whitespace-pre-wrap pl-2">{content}</p>
      </div>
    </div>
  );
}

function RoomSuggestionCard({ room, onSelect, hotelPhotos = [], roomTypes = [] }) {
  // Try to find room type image first, then Argus photo, then fallback
  const matchedRoomType = roomTypes.find(rt => 
    rt.name === room.name || rt.name === room.room_type_name || rt.name === room.room_type
  );
  const roomTypeImage = matchedRoomType?.hero_image || matchedRoomType?.images?.[0];
  const roomPhoto = hotelPhotos.find(p => p.category === 'room')?.variants?.card?.path;
  const photoSrc = roomTypeImage
    || room.image_url 
    || (roomPhoto ? `${BACKEND_URL}/api/photos/serve/${roomPhoto}` : null)
    || `https://picsum.photos/seed/${room.id}/600/400`;
  return (
    <Card className="min-w-[260px] max-w-[280px] room-card-hover cursor-pointer border border-border/60 overflow-hidden"
          data-testid="booking-room-suggestion-card"
          onClick={() => onSelect(room)}>
      <div className="relative h-32 overflow-hidden">
        <img src={photoSrc}
             alt={room.name} className="w-full h-full object-cover" />
        <Badge className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px]">
          <ShieldCheck className="w-3 h-3 mr-1" /> Available
        </Badge>
      </div>
      <CardContent className="p-3">
        <h4 className="font-semibold text-sm heading-font">{room.name}</h4>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <Users className="w-3 h-3" /> Up to {room.max_guests}
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {(room.amenities || []).slice(0, 3).map((a, i) => (
            <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">{a}</Badge>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-lg font-bold heading-font mono">${room.price_per_night}<span className="text-xs font-normal text-muted-foreground">/night</span></span>
          <Button size="sm" className="h-7 text-xs" data-testid="booking-room-select-button">Select</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentCard({ bookingState, pricingInfo, hotel, onPay, onPayAtProperty, onAddToCart, onPayGasless, loading }) {
  const [paymentMethod, setPaymentMethod] = useState('crypto');
  const [selectedChain, setSelectedChain] = useState('base'); // Changed default to Base (lowest fees)
  const [addingToCart, setAddingToCart] = useState(false);
  
  const handlePayment = () => {
    if (paymentMethod === 'crypto') {
      onPay('crypto', selectedChain);
    } else if (paymentMethod === 'gasless') {
      onPayGasless();
    } else {
      onPayAtProperty();
    }
  };
  
  const handleAddToCart = async () => {
    setAddingToCart(true);
    try {
      await onAddToCart();
    } finally {
      setAddingToCart(false);
    }
  };
  
  return (
    <Card className="border-2 border-primary/20 bg-card shadow-lg message-enter" data-testid="booking-payment-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-primary" />
          <h3 className="font-semibold heading-font">Choose Payment Method</h3>
        </div>
        
        {/* Payment Method Selector */}
        <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="mb-4">
          <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-accent/50 cursor-pointer">
            <RadioGroupItem value="crypto" id="pay-crypto" data-testid="checkout-payment-method-pay-with-crypto" />
            <Label htmlFor="pay-crypto" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                <div>
                  <p className="font-medium text-sm">Pay with Crypto</p>
                  <p className="text-xs text-muted-foreground">Pay securely from your wallet. Confirmation usually takes 10–90 seconds.</p>
                </div>
              </div>
            </Label>
          </div>
          
          <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-accent/50 cursor-pointer">
            <RadioGroupItem value="property" id="pay-property" data-testid="checkout-payment-method-pay-at-property" />
            <Label htmlFor="pay-property" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <div>
                  <p className="font-medium text-sm">Pay at Property</p>
                  <p className="text-xs text-muted-foreground">Confirm with phone + last 4 card digits before check-in.</p>
                </div>
              </div>
            </Label>
          </div>

          <div className="flex items-center space-x-2 p-3 rounded-lg border-2 border-[hsl(184,78%,34%)]/30 bg-[hsl(184,78%,34%)]/5 hover:bg-[hsl(184,78%,34%)]/10 cursor-pointer">
            <RadioGroupItem value="gasless" id="pay-gasless" data-testid="checkout-payment-method-gasless" />
            <Label htmlFor="pay-gasless" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[hsl(184,78%,34%)]" />
                <div>
                  <p className="font-medium text-sm flex items-center gap-1.5">
                    One-Click Gasless
                    <span className="text-[10px] bg-[hsl(184,78%,34%)]/10 text-[hsl(184,78%,34%)] px-1.5 py-0.5 rounded-full font-semibold">AA</span>
                  </p>
                  <p className="text-xs text-muted-foreground">$0 gas fees. Protocol sponsors your transaction via ERC-4337.</p>
                </div>
              </div>
            </Label>
          </div>
        </RadioGroup>
        
        {/* Network Selector (only for crypto) */}
        {paymentMethod === 'crypto' && (
          <div className="mb-4 space-y-2">
            <Label htmlFor="network-select" className="text-sm">Select Blockchain Network</Label>
            <Select value={selectedChain} onValueChange={setSelectedChain}>
              <SelectTrigger id="network-select" data-testid="crypto-network-select-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid="crypto-network-select">
                <SelectItem value="ethereum">Ethereum (ETH)</SelectItem>
                <SelectItem value="base">Base (ETH L2)</SelectItem>
                <SelectItem value="polygon">Polygon (MATIC)</SelectItem>
                <SelectItem value="bitcoin">Bitcoin (BTC)</SelectItem>
                <SelectItem value="solana">Solana (SOL)</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Network Fee Warning */}
            <div className={`p-3 rounded-lg border mt-3 ${
              selectedChain === 'ethereum' ? 'bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800' :
              selectedChain === 'bitcoin' ? 'bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800' :
              'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'
            }`}>
              <div className="flex items-start gap-2">
                {selectedChain === 'ethereum' || selectedChain === 'bitcoin' ? (
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-600" />
                ) : (
                  <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
                )}
                <div className="flex-1 text-sm">
                  {selectedChain === 'ethereum' && (
                    <>
                      <p className="font-medium text-orange-700 dark:text-orange-400">High network fees ($5-25)</p>
                      <p className="text-xs text-orange-600/80 dark:text-orange-400/80 mt-1">Consider using Base for lower fees (under $1)</p>
                    </>
                  )}
                  {selectedChain === 'base' && (
                    <>
                      <p className="font-medium text-green-700 dark:text-green-400">✅ Recommended - Low fees (~$0.50)</p>
                      <p className="text-xs text-green-600/80 dark:text-green-400/80 mt-1">Fast confirmation, lowest cost</p>
                    </>
                  )}
                  {selectedChain === 'polygon' && (
                    <>
                      <p className="font-medium text-green-700 dark:text-green-400">✅ Low fees (~$1)</p>
                      <p className="text-xs text-green-600/80 dark:text-green-400/80 mt-1">Fast and affordable</p>
                    </>
                  )}
                  {selectedChain === 'bitcoin' && (
                    <>
                      <p className="font-medium text-orange-700 dark:text-orange-400">Slow confirmation (30+ min) + High fees ($5-30)</p>
                      <p className="text-xs text-orange-600/80 dark:text-orange-400/80 mt-1">Consider Base or Polygon for instant confirmation</p>
                    </>
                  )}
                  {selectedChain === 'solana' && (
                    <>
                      <p className="font-medium text-green-700 dark:text-green-400">✅ Very low fees (~$0.01)</p>
                      <p className="text-xs text-green-600/80 dark:text-green-400/80 mt-1">Ultra-fast confirmation</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {pricingInfo && (
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Base price ({pricingInfo.nights} night{pricingInfo.nights > 1 ? 's' : ''})</span>
              <span className="mono">${pricingInfo.base_total}</span>
            </div>
            {pricingInfo.discount_pct > 0 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>{pricingInfo.label} discount (-{pricingInfo.discount_pct}%)</span>
                <span className="mono">-${pricingInfo.discount_amount}</span>
              </div>
            )}
            {pricingInfo.surcharge_pct > 0 && (
              <div className="flex justify-between text-sm text-amber-600">
                <span>Last minute surcharge (+{pricingInfo.surcharge_pct}%)</span>
                <span className="mono">+${pricingInfo.surcharge_amount}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="mono text-lg" data-testid="booking-price-total">${pricingInfo.final_price}</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {pricingInfo.label} — {pricingInfo.refund_pct}% refund if cancelled
            </Badge>
          </div>
        )}

        {/* Cancellation Policy - Compact Reminder */}
        {hotel && (
          <div className="mb-4">
            <CancellationPolicyCard 
              hotelId={hotelId}
              hotel={hotel}
              guestEmail={bookingState.guest_email || null}
              compact={true}
            />
          </div>
        )}

        {/* Simple Refund Scenarios - Before Payment */}
        {pricingInfo && bookingState.check_in && (
          <div className="mb-4">
            <RefundScenarios 
              booking={{
                final_price: pricingInfo.final_price,
                locked_refund_pct: pricingInfo.refund_pct || 50,
                check_in: bookingState.check_in,
                pricing_tier: pricingInfo.label || 'Standard',
                guest_email: bookingState.guest_email || userEmail
              }}
              showCancelButton={false}
            />
          </div>
        )}
        
        <div className="space-y-2">
          <Button 
            className="w-full h-12" 
            onClick={handlePayment} 
            disabled={loading || addingToCart} 
            data-testid="booking-pay-button"
          >
            {paymentMethod === 'crypto' ? (
              <>
                <Wallet className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Continue to Payment'}
              </>
            ) : paymentMethod === 'gasless' ? (
              <>
                <Zap className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Book Gasless ($0 Gas)'}
              </>
            ) : (
              <>
                <Phone className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Book with Pay at Property'}
              </>
            )}
          </Button>
          
          {/* Add to Cart Button */}
          <Button 
            variant="outline"
            className="w-full h-12 border-primary/40 hover:bg-primary/5"
            onClick={handleAddToCart} 
            disabled={loading || addingToCart} 
            data-testid="booking-add-to-cart-button"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {addingToCart ? 'Adding...' : 'Add to Cart & Continue Browsing'}
          </Button>
        </div>
        
        <p className="text-[11px] text-center text-muted-foreground mt-2">
          {paymentMethod === 'crypto' ? 'Connect your wallet on the next screen' : 'Your booking is protected'}
        </p>
      </CardContent>
    </Card>
  );
}

function BookingConfirmation({ booking, pricingInfo, payment, hotel, nftMintingInfo }) {
  const [showNFTOptIn, setShowNFTOptIn] = useState(false);
  const [nftChain, setNFTChain] = useState('base');
  const [guestWallet, setGuestWallet] = useState('');
  const [minting, setMinting] = useState(false);
  const [mintResult, setMintResult] = useState(null);

  useEffect(() => {
    // Show NFT opt-in only for crypto bookings without existing NFT
    if (booking?.payment_method === 'crypto' && !booking?.nft_token_id && nftMintingInfo?.eligible_for_nft) {
      setShowNFTOptIn(true);
      setNFTChain(nftMintingInfo.chain || 'base');
    }
  }, [booking, nftMintingInfo]);

  const handleMintNFT = async () => {
    if (!guestWallet || !guestWallet.match(/^0x[a-fA-F0-9]{40}$/)) {
      toast.error('Please enter a valid Ethereum wallet address');
      return;
    }

    try {
      setMinting(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/bookings/${booking.id}/mint-nft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_wallet: guestWallet,
          chain: nftChain
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setMintResult(result.nft);
        toast.success('NFT receipt minted successfully!');
        setShowNFTOptIn(false);
      } else {
        toast.error(result.message || 'Failed to mint NFT');
      }
    } catch (error) {
      console.error('NFT minting error:', error);
      toast.error('Failed to mint NFT receipt');
    } finally {
      setMinting(false);
    }
  };

  const getNFTCostInfo = (chain) => {
    const costs = {
      base: { fee: 4.0, warning: null, badge: 'Recommended' },
      polygon: { fee: 3.5, warning: null, badge: 'Cheapest' },
      ethereum: { fee: 28.0, warning: 'High gas fees on Ethereum mainnet', badge: 'Expensive' }
    };
    return costs[chain] || costs.base;
  };

  const handlePrintReceipt = () => {
    const receiptWindow = window.open('', '_blank', 'width=600,height=800');
    receiptWindow.document.write(`
      <!DOCTYPE html><html><head><title>Booking Receipt - getbitsy.ai</title>
      <style>
        body { font-family: 'Segoe UI', sans-serif; padding: 40px; max-width: 500px; margin: 0 auto; color: #1a1a2e; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #0ea5e9; padding-bottom: 20px; }
        .logo { font-size: 24px; font-weight: bold; color: #0ea5e9; }
        .confirmed { color: #059669; font-size: 18px; margin-top: 8px; }
        .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; font-size: 14px; }
        .row .label { color: #666; }
        .row .value { font-weight: 500; }
        .total { font-size: 20px; font-weight: bold; margin-top: 10px; }
        .footer { text-align: center; margin-top: 30px; font-size: 11px; color: #999; }
        .tx { word-break: break-all; font-family: monospace; font-size: 11px; background: #f5f5f5; padding: 8px; border-radius: 6px; margin-top: 10px; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      <div class="header">
        <div class="logo">getbitsy.ai</div>
        <div class="confirmed">Booking Confirmed</div>
      </div>
      <div class="row"><span class="label">Booking ID</span><span class="value" style="font-family:monospace;font-size:12px">${booking?.id || ''}</span></div>
      <div class="row"><span class="label">Hotel</span><span class="value">${hotel?.name || ''}</span></div>
      <div class="row"><span class="label">Location</span><span class="value">${hotel?.city || ''}, ${hotel?.country || ''}</span></div>
      <div class="row"><span class="label">Guest</span><span class="value">${booking?.guest_name || ''}</span></div>
      <div class="row"><span class="label">Email</span><span class="value">${booking?.guest_email || ''}</span></div>
      <div class="row"><span class="label">Check-in</span><span class="value">${booking?.check_in || ''}</span></div>
      <div class="row"><span class="label">Check-out</span><span class="value">${booking?.check_out || ''}</span></div>
      <div class="row"><span class="label">Guests</span><span class="value">${booking?.guests || 1}</span></div>
      <div class="row"><span class="label">Pricing Tier</span><span class="value">${pricingInfo?.label || booking?.pricing_tier || ''}</span></div>
      <div class="row"><span class="label">Refund Policy</span><span class="value">${booking?.refund_pct || pricingInfo?.refund_pct || 0}% refundable</span></div>
      <div class="row total"><span>Total Paid</span><span>$${booking?.final_price || 0}</span></div>
      ${payment?.tx_hash ? `<div class="tx"><strong>Transaction Hash:</strong><br>${payment.tx_hash}</div>` : ''}
      <div class="footer">
        <p>Protected by getbitsy.ai Escrow</p>
        <p>Generated on ${new Date().toLocaleString()}</p>
      </div>
      </body></html>
    `);
    receiptWindow.document.close();
    setTimeout(() => receiptWindow.print(), 500);
  };

  const costInfo = getNFTCostInfo(nftChain);

  return (
    <div className="space-y-3">
      <Card className="border-2 border-emerald-200 bg-emerald-50/50 message-enter" data-testid="booking-confirmation">
        <CardContent className="p-5 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold heading-font mb-1">Booking Confirmed!</h3>
          <p className="text-sm text-muted-foreground mb-4">Your reservation is secured.</p>
          <div className="bg-white rounded-xl p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Booking ID</span><span className="mono text-xs">{booking?.id?.slice(0, 12)}...</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Check-in</span><span>{booking?.check_in}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Check-out</span><span>{booking?.check_out}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total Paid</span><span className="font-bold mono">${booking?.final_price}</span></div>
            {pricingInfo && (
              <div className="flex justify-between"><span className="text-muted-foreground">Tier</span><Badge variant="outline" className="text-xs">{pricingInfo.label} — {pricingInfo.refund_pct}% refund</Badge></div>
            )}
            {booking?.payment_chain && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Blockchain</span>
                <Badge variant="outline" className="text-xs uppercase">{booking.payment_chain}</Badge>
              </div>
            )}
            {payment?.tx_hash && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transaction</span>
                  <span className="mono text-xs">{payment.tx_hash.slice(0, 16)}...</span>
                </div>
                {booking?.payment_chain && (
                  <a 
                    href={getExplorerLink(booking.payment_chain, payment.tx_hash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 justify-center"
                    data-testid="booking-explorer-link"
                  >
                    View on block explorer <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </>
            )}
          </div>
          <Button variant="outline" size="sm" className="mt-4" onClick={handlePrintReceipt} data-testid="booking-print-receipt">
            <Receipt className="w-4 h-4 mr-2" /> Download Receipt
          </Button>
        </CardContent>
      </Card>

      {/* NFT Opt-In Card */}
      {showNFTOptIn && !mintResult && (
        <Card className="border border-[hsl(var(--crypto))] bg-gradient-to-br from-[hsl(var(--crypto))]/5 to-transparent message-enter" data-testid="nft-opt-in-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--crypto))]/10 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-5 h-5 text-[hsl(var(--crypto))]" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold heading-font text-sm mb-1">Mint Resellable Stay NFT (Optional)</h4>
                <p className="text-xs text-muted-foreground">Convert your booking into an NFT that you can resell on marketplaces like OpenSea before check-in.</p>
              </div>
            </div>

            {/* Chain Selector */}
            <div className="space-y-3 mt-4">
              <div>
                <Label htmlFor="nft-chain-select" className="text-xs text-muted-foreground mb-1.5 block">Select Blockchain</Label>
                <Select value={nftChain} onValueChange={setNFTChain}>
                  <SelectTrigger id="nft-chain-select" data-testid="nft-chain-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="base">
                      <div className="flex items-center justify-between w-full">
                        <span>Base</span>
                        <Badge variant="outline" className="text-[9px] ml-2">Recommended</Badge>
                      </div>
                    </SelectItem>
                    <SelectItem value="polygon">
                      <div className="flex items-center justify-between w-full">
                        <span>Polygon</span>
                        <Badge variant="outline" className="text-[9px] ml-2">Cheapest</Badge>
                      </div>
                    </SelectItem>
                    <SelectItem value="ethereum">
                      <div className="flex items-center justify-between w-full">
                        <span>Ethereum</span>
                        <Badge variant="destructive" className="text-[9px] ml-2">High Fees</Badge>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Fee Display */}
              <div className="bg-card/60 border border-border rounded-xl p-3">
                <div className="flex justify-between items-center text-sm mb-1">
                  <span className="text-muted-foreground">Minting Fee</span>
                  <span className="font-semibold mono">${costInfo.fee.toFixed(2)}</span>
                </div>
                {costInfo.warning && (
                  <div className="flex items-start gap-2 mt-2 pt-2 border-t border-border">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-orange-600">{costInfo.warning}</p>
                  </div>
                )}
              </div>

              {/* Wallet Input */}
              <div>
                <Label htmlFor="guest-wallet-input" className="text-xs text-muted-foreground mb-1.5 block">Your Wallet Address</Label>
                <Input 
                  id="guest-wallet-input"
                  data-testid="nft-guest-wallet-input"
                  placeholder="0x..."
                  value={guestWallet}
                  onChange={(e) => setGuestWallet(e.target.value)}
                  className="mono text-xs"
                />
                <p className="text-[10px] text-muted-foreground mt-1">The NFT will be sent to this address. Make sure you own this wallet.</p>
              </div>

              {/* Mint Button */}
              <Button 
                onClick={handleMintNFT}
                disabled={minting || !guestWallet}
                className="w-full"
                data-testid="nft-mint-button"
              >
                {minting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Minting NFT...
                  </>
                ) : (
                  <>
                    <Wallet className="w-4 h-4 mr-2" />
                    Mint NFT Receipt (${costInfo.fee})
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* NFT Minted Success */}
      {mintResult && (
        <Card className="border-2 border-[hsl(var(--crypto))] bg-gradient-to-br from-[hsl(var(--crypto))]/10 to-transparent message-enter" data-testid="nft-minted-success">
          <CardContent className="p-5">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[hsl(var(--crypto))]/20 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-[hsl(var(--crypto))]" />
              </div>
              <h4 className="font-semibold heading-font mb-2">NFT Receipt Minted!</h4>
              <p className="text-xs text-muted-foreground mb-4">Your booking is now a resellable NFT on {nftChain.charAt(0).toUpperCase() + nftChain.slice(1)}.</p>
              
              <div className="bg-card/60 border border-border rounded-xl p-3 text-left space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Token ID</span>
                  <span className="mono font-semibold">#{mintResult.token_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contract</span>
                  <span className="mono text-[10px]">{mintResult.contract_address?.slice(0, 8)}...{mintResult.contract_address?.slice(-6)}</span>
                </div>
                {mintResult.royalty_percent && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Hotel Royalty</span>
                    <span className="font-semibold">{mintResult.royalty_percent}%</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 text-xs"
                  onClick={() => window.open(mintResult.opensea_url, '_blank')}
                  data-testid="nft-view-opensea-button"
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> View on OpenSea
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 text-xs"
                  onClick={() => window.open(mintResult.explorer_url, '_blank')}
                  data-testid="nft-view-explorer-button"
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> Block Explorer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Confirmation Card */}
      {!showNFTOptIn && !mintResult && (
        <Card className="border-2 border-emerald-200 bg-emerald-50/50 message-enter" data-testid="booking-confirmation-main">
          <CardContent className="p-5 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold heading-font mb-1">Booking Confirmed!</h3>
            <p className="text-sm text-muted-foreground mb-4">Your reservation is secured.</p>
            <div className="bg-white rounded-xl p-4 text-left space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Booking ID</span><span className="mono text-xs">{booking?.id?.slice(0, 12)}...</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Check-in</span><span>{booking?.check_in}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Check-out</span><span>{booking?.check_out}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Paid</span><span className="font-bold mono">${booking?.final_price}</span></div>
              {pricingInfo && (
                <div className="flex justify-between"><span className="text-muted-foreground">Tier</span><Badge variant="outline" className="text-xs">{pricingInfo.label} — {pricingInfo.refund_pct}% refund</Badge></div>
              )}
              {booking?.payment_chain && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Blockchain</span>
                  <Badge variant="outline" className="text-xs uppercase">{booking.payment_chain}</Badge>
                </div>
              )}
              {payment?.tx_hash && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transaction</span>
                    <span className="mono text-xs">{payment.tx_hash.slice(0, 16)}...</span>
                  </div>
                  {booking?.payment_chain && (
                    <a 
                      href={getExplorerLink(booking.payment_chain, payment.tx_hash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1 justify-center"
                      data-testid="booking-explorer-link"
                    >
                      View on block explorer <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </>
              )}
            </div>
            <Button variant="outline" size="sm" className="mt-4" onClick={handlePrintReceipt} data-testid="booking-print-receipt">
              <Receipt className="w-4 h-4 mr-2" /> Download Receipt
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TripSummary({ bookingState, hotel, pricingBreakdown }) {
  return (
    <div className="space-y-4">
      {/* Guest Information */}
      {(bookingState?.guest_name || bookingState?.guest_email) && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Guest Information</h4>
          {bookingState.guest_name && (
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{bookingState.guest_name}</p>
              </div>
            </div>
          )}
          {bookingState.guest_email && (
            <div className="flex items-start gap-2">
              <span className="text-sm text-muted-foreground flex-shrink-0 ml-6">{bookingState.guest_email}</span>
            </div>
          )}
          {bookingState.guest_phone && (
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">{bookingState.guest_phone}</span>
            </div>
          )}
        </div>
      )}

      {/* Booking Details */}
      {(bookingState?.check_in || bookingState?.room_type) && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your Stay</h4>
          {hotel && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">{hotel.name}</span>
            </div>
          )}
          {bookingState?.check_in && (
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p>{bookingState.check_in} → {bookingState.check_out || '...'}</p>
                {pricingBreakdown?.nights && (
                  <p className="text-xs text-muted-foreground">{pricingBreakdown.nights} night{pricingBreakdown.nights !== 1 ? 's' : ''}</p>
                )}
              </div>
            </div>
          )}
          {bookingState?.guests && (
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm">{bookingState.guests} guest{bookingState.guests > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}

      {/* Selected Room */}
      {bookingState?.room_type && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
          <p className="text-xs text-muted-foreground mb-1">Selected Room</p>
          <p className="font-semibold text-sm">{bookingState.room_type}</p>
        </div>
      )}

      {/* Pricing Breakdown */}
      {pricingBreakdown && pricingBreakdown.breakdown && pricingBreakdown.breakdown.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-border">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <span>💰</span> Price Breakdown
          </h4>
          
          {/* Day-by-day rates */}
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {pricingBreakdown.breakdown.map((day, idx) => (
              <div key={idx} className="flex justify-between items-center text-xs py-1">
                <span className="text-muted-foreground">
                  {day.day_name}, {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {day.rate_type !== 'standard' && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${
                      day.rate_type === 'weekend' 
                        ? 'bg-primary/10 text-primary' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {day.rate_type}
                    </span>
                  )}
                </span>
                <span className="font-mono font-medium">${day.rate.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Subtotal, Tax, Total */}
          <div className="space-y-1 pt-2 border-t border-border">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">${pricingBreakdown.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Tax ({(pricingBreakdown.tax_rate * 100).toFixed(0)}%)</span>
              <span className="font-mono">${pricingBreakdown.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-base font-semibold pt-2 border-t border-border">
              <span>Total</span>
              <span className="font-mono text-primary">${pricingBreakdown.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BookingPage() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isOpen: cartOpen, toggleCart, setIsOpen: setCartOpen } = useCartDrawer();
  const [hotel, setHotel] = useState(null);
  const [allRooms, setAllRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]); // Rooms suggested by AI
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(genSessionId);
  const [bookingState, setBookingState] = useState({});
  const [pricingInfo, setPricingInfo] = useState(null);
  const [pricingBreakdown, setPricingBreakdown] = useState(null); // Detailed day-by-day breakdown
  const [booking, setBooking] = useState(null);
  const [payment, setPayment] = useState(null);
  const [nftMintingInfo, setNFTMintingInfo] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [showCryptoPayment, setShowCryptoPayment] = useState(false);
  const [showGaslessCheckout, setShowGaslessCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false); // Track when to show payment on right
  const [selectedChain, setSelectedChain] = useState('base'); // Changed default to Base (lowest fees)
  const [walletAddresses, setWalletAddresses] = useState(null);
  const [previewImage, setPreviewImage] = useState(null); // For gallery image preview
  const [hotelPhotos, setHotelPhotos] = useState([]); // Real photos from Argus
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [guestId, setGuestId] = useState(null);
  const [userEmail, setUserEmail] = useState(null); // Track logged-in guest email for reliability
  const [hotelLoadError, setHotelLoadError] = useState(null); // Surface API failures instead of hanging forever
  const [hotelLoadAttempt, setHotelLoadAttempt] = useState(0); // Used to trigger manual retry
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Listen for cart drawer open events
  useEffect(() => {
    const handleOpenCart = () => setCartOpen(true);
    window.addEventListener('openCartDrawer', handleOpenCart);
    return () => window.removeEventListener('openCartDrawer', handleOpenCart);
  }, [setCartOpen]);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    setHotelLoadError(null);
    hotelsApi.getPublic(hotelId).then(({ data }) => {
      if (cancelled) return;
      if (!data?.hotel) {
        setHotelLoadError({ kind: 'empty', message: 'Concierge could not find this property.' });
        return;
      }
      setHotel(data.hotel);
      setAllRooms(data.rooms);
      // Store room types with images
      if (data.room_types?.length) {
        setRoomTypes(data.room_types);
      }
      // Store real hotel photos from Argus
      if (data.photos?.length) {
        setHotelPhotos(data.photos);
        // Set hero photo as initial preview
        const hero = data.photos.find(p => p.is_hero) || data.photos[0];
        if (hero?.variants?.hero?.path) {
          setPreviewImage(`${BACKEND_URL}/api/photos/serve/${hero.variants.hero.path}`);
        }
      } else if (data.hotel?.hero_photo_serve) {
        setPreviewImage(`${BACKEND_URL}/api/photos/serve/${data.hotel.hero_photo_serve}`);
      }
    }).catch((err) => {
      if (cancelled) return;
      console.error('[BookingPage] hotels.getPublic failed', err);
      const status = err?.response?.status;
      setHotelLoadError({
        kind: 'network',
        status,
        message: status
          ? `Concierge service returned ${status}. Please retry or pick another property.`
          : 'Could not reach the concierge service. Check your connection and try again.',
      });
    });

    // Load wallet addresses (non-blocking – silent failure is fine here)
    hotelsApi.getWalletAddresses(hotelId).then((data) => {
      if (!cancelled) setWalletAddresses(data.wallet_addresses);
    }).catch(console.error);

    // Load user email for reliability-adjusted pricing
    const email = localStorage.getItem('userEmail');
    if (email) setUserEmail(email);

    return () => { cancelled = true; };
  }, [hotelId, hotelLoadAttempt]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Fetch pricing breakdown when room and dates are selected
  useEffect(() => {
    const fetchPricingBreakdown = async () => {
      if (bookingState.room_id && bookingState.check_in && bookingState.check_out) {
        try {
          const backendUrl = BACKEND_URL;
          const response = await fetch(`${backendUrl}/api/bookings/calculate-price`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              room_id: bookingState.room_id,
              check_in: bookingState.check_in,
              check_out: bookingState.check_out
            })
          });
          const data = await response.json();
          if (data.success) {
            setPricingBreakdown(data.pricing);
          }
        } catch (error) {
          console.error('Failed to fetch pricing breakdown:', error);
        }
      }
    };

    fetchPricingBreakdown();
  }, [bookingState.room_id, bookingState.check_in, bookingState.check_out]);

  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  // Auto-show payment when all booking details are ready
  useEffect(() => {
    const shouldShowPayment = bookingState.status === 'ready_to_book' ||
      (bookingState.room_id && bookingState.guest_name && bookingState.guest_email && bookingState.check_in && bookingState.check_out);
    setShowPayment(shouldShowPayment);
  }, [bookingState]);

  const sendMessage = async (text, messageContext = null) => {
    if (!text.trim() || !hotelId) return;
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const payload = { hotel_id: hotelId, session_id: sessionId, message: text.trim() };
      if (messageContext) payload.context = messageContext;
      const { data } = await chatApi.send(payload);
      const aiMsg = { 
        role: 'assistant', 
        content: data.message,
        isPersonalized: data.used_personalization || false,
        personalizationSources: data.personalization_sources || []
      };
      setMessages(prev => [...prev, aiMsg]);
      if (data.booking_state) setBookingState(data.booking_state);
      speak(data.message);

      // Show hotel gallery proactively on first message
      if (data.hotel_gallery && data.hotel_gallery.length > 0) {
        setMessages(prev => [...prev, { role: 'gallery', images: data.hotel_gallery }]);
      }

      if (data.suggested_rooms?.length > 0) {
        setAvailableRooms(data.suggested_rooms); // Store for right panel
      }

      // Auto-trigger payment when Bitsy sets process_payment action
      if (data.action === 'process_payment' && data.booking_state?.payment_method && data.booking_state?.room_id) {
        const payMethod = data.booking_state.payment_method;
        if (payMethod === 'crypto') {
          handlePay('crypto', 'base');
        } else {
          handlePay('pay_at_property');
        }
      }
      // Auto-calculate pricing when room is selected
      else if (data.booking_state?.check_in && data.booking_state?.check_out && data.booking_state?.room_id) {
        const selectedRoom = allRooms.find(r => r.id === data.booking_state.room_id);
        if (selectedRoom) {
          try {
            const ci = new Date(data.booking_state.check_in);
            const co = new Date(data.booking_state.check_out);
            const nights = Math.max(Math.round((co - ci) / 86400000), 1);
            const { data: pd } = await pricingApi.calculate(
              data.booking_state.check_in, 
              selectedRoom.price_per_night, 
              nights,
              data.booking_state.guest_email || null,
              hotel?.id || null
            );
            setPricingInfo(pd);
            setShowPayment(true); // Show payment on right panel
          } catch (e) { console.error(e); }
        }
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had a little hiccup. Could you try again?' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSelect = (room) => {
    setBookingState(prev => ({ ...prev, room_id: room.id, room_type: room.name }));
    // Update preview image to show the selected room's image
    if (room.image_url) {
      setPreviewImage(room.image_url);
    }
    sendMessage(`I'll take the ${room.name} please!`);
  };

  const checkAndShowPreferenceModal = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
            const countResponse = await fetch(`${BACKEND_URL}/api/guests/me/booking-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (countResponse.ok) {
        const countData = await countResponse.json();
        
        // If this is their first completed booking, show preference modal
        if (countData.booking_count === 1) {
          // Small delay for better UX (let them see confirmation first)
          setTimeout(() => setShowPreferenceModal(true), 2500);
        }
      }
    } catch (error) {
      console.error('Error checking booking count:', error);
    }
  };

  const handlePay = async (method = 'crypto', chain = 'ethereum') => {
    if (!bookingState.guest_name || !bookingState.guest_email) {
      sendMessage('I\'d like to pay now');
      return;
    }
    
    // Check if hotel has wallet address for selected chain
    if (method === 'crypto' && walletAddresses) {
      const hotelWallet = walletAddresses[chain];
      if (!hotelWallet || !hotelWallet.trim()) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, this hotel hasn't configured ${chain} payments yet. Please try a different network or pay at property.` }]);
        return;
      }
    }
    
    setPayLoading(true);
    setSelectedChain(chain);
    try {
      const { data } = await bookingsApi.create({
        hotel_id: hotelId,
        room_id: bookingState.room_id,
        guest_name: bookingState.guest_name,
        guest_email: bookingState.guest_email,
        guest_phone: bookingState.guest_phone || '',
        check_in: bookingState.check_in,
        check_out: bookingState.check_out,
        guests: parseInt(bookingState.guests) || 1,
        payment_method: method,
        payment_chain: method === 'crypto' ? chain : null,
        special_requests: bookingState.special_requests || ''
      });
      setBooking(data.booking);
      setPayment(data.payment);
      setPricingInfo(data.pricing);
      setNFTMintingInfo(data.nft_minting_info || null);
      
      // If crypto payment, show crypto payment screen
      if (method === 'crypto') {
        setShowCryptoPayment(true);
      } else if (method === 'pay_at_property') {
        // For pay at property, check if first booking immediately
        checkAndShowPreferenceModal();
      }
      // Confirmation is now shown on right panel via booking state, not in chat messages
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong with the payment. Please try again.' }]);
    } finally {
      setPayLoading(false);
    }
  };

  const handleAddToCart = async () => {
    // Get selected room details
    const selectedRoom = allRooms.find(r => r.id === bookingState.room_id);
    if (!selectedRoom) {
      toast.error('Room information not found');
      return;
    }

    // Calculate nights
    const checkIn = new Date(bookingState.check_in);
    const checkOut = new Date(bookingState.check_out);
    const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

    // Prepare cart item data
    const cartItemData = {
      hotel_id: hotelId,
      hotel_name: hotel.name,
      room_type: selectedRoom.name,
      room_id: bookingState.room_id,
      check_in: bookingState.check_in,
      check_out: bookingState.check_out,
      nights: nights,
      guests: parseInt(bookingState.guests) || 1,
      base_price: pricingBreakdown?.final_price || selectedRoom.price_per_night * nights
    };

    // Add to cart
    const result = await addToCart('guest_room', cartItemData);
    
    if (result.success) {
      // Reset booking state to allow adding more items
      setBookingState({});
      setPricingInfo(null);
      setPricingBreakdown(null);
      setShowPayment(false);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Great! I've added ${selectedRoom.name} to your cart. You can continue browsing to add more rooms or meeting spaces, then checkout when ready!` 
      }]);
    }
  };

  const toggleVoice = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Voice not supported in this browser');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      sendMessage(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  if (!hotel) {
    if (hotelLoadError) {
      return (
        <div className="lobby-theme h-screen flex items-center justify-center px-6" data-testid="booking-concierge-error">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 rounded-full bg-[color:var(--l-copper)]/20 mx-auto mb-4 flex items-center justify-center">
              <span className="text-[color:var(--l-copper)] text-xl font-semibold">!</span>
            </div>
            <h2 className="text-xl font-semibold text-[color:var(--l-cream-1)] mb-2">Concierge unavailable</h2>
            <p className="text-sm text-[color:var(--l-cream-2)] mb-6">{hotelLoadError.message}</p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => setHotelLoadAttempt((n) => n + 1)}
                data-testid="booking-concierge-retry-btn"
                className="px-4 py-2 rounded-full bg-[color:var(--l-copper)] text-[color:var(--l-ink)] text-sm font-medium hover:opacity-90"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                data-testid="booking-concierge-home-btn"
                className="px-4 py-2 rounded-full border border-[color:var(--l-cream-2)]/40 text-[color:var(--l-cream-1)] text-sm font-medium hover:bg-[color:var(--l-cream-1)]/5"
              >
                Back to Lobby
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="lobby-theme h-screen flex items-center justify-center" data-testid="booking-concierge-loading">
        <div className="text-center">
          <Sparkles className="w-8 h-8 text-[color:var(--l-copper)] mx-auto mb-3 animate-pulse" />
          <p className="text-[color:var(--l-cream-2)]">Finding your concierge…</p>
        </div>
      </div>
    );
  }
  
  // Show gasless checkout screen
  if (showGaslessCheckout) {
    return (
      <GaslessCheckout
        bookingState={bookingState}
        pricingInfo={pricingInfo}
        hotel={hotel}
        hotelId={hotelId}
        onSuccess={(result) => {
          setShowGaslessCheckout(false);
          setBooking(result.booking);
          setMessages(prev => [...prev, {
            role: 'confirmation',
            booking: result.booking,
            pricing: pricingInfo,
            payment: { tx_hash: result.tx_hash, gas_cost: '0' }
          }]);
          checkAndShowPreferenceModal();
        }}
        onCancel={() => {
          setShowGaslessCheckout(false);
          setMessages(prev => [...prev, { role: 'assistant', content: 'Gasless checkout cancelled. You can try again or choose another payment method.' }]);
        }}
      />
    );
  }

  // Show crypto payment screen if booking created and crypto selected
  if (showCryptoPayment && booking && walletAddresses) {
    const hotelWallet = walletAddresses[selectedChain];
    const isTestnet = process.env.NODE_ENV === 'development';
    
    return (
      <CryptoPayment
        bookingId={booking.id}
        hotelWalletAddress={hotelWallet}
        amountUSD={pricingInfo?.final_price || 0}
        chain={selectedChain}
        testnetMode={isTestnet}
        onSuccess={async (txData) => {
          setShowCryptoPayment(false);
          setMessages(prev => [...prev, { 
            role: 'confirmation', 
            booking: { ...booking, tx_hash: txData.tx_hash, payment_chain: selectedChain }, 
            pricing: pricingInfo, 
            payment: { ...payment, tx_hash: txData.tx_hash } 
          }]);
          
          // Check if this is first booking and trigger preference modal
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const countResponse = await fetch(`${BACKEND_URL}/api/guests/me/booking-count`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (countResponse.ok) {
                const countData = await countResponse.json();
                
                // If this is their first completed booking, show preference modal
                if (countData.booking_count === 1) {
                  // Small delay for better UX (let them see confirmation first)
                  setTimeout(() => setShowPreferenceModal(true), 2000);
                }
              }
            }
          } catch (error) {
            console.error('Error checking booking count:', error);
          }
        }}
        onCancel={() => {
          setShowCryptoPayment(false);
          setMessages(prev => [...prev, { role: 'assistant', content: 'Payment cancelled. You can try again or choose to pay at the property.' }]);
        }}
      />
    );
  }

  // Computed: Check if we should show payment based on booking state
  // Moved to useEffect to update showPayment state

  return (
    <div className="lobby-theme h-screen flex flex-col" data-testid="booking-page">
      {/* Header */}
      <header className="border-b border-[color:var(--l-line)] bg-[color:var(--l-bg)]/85 backdrop-blur-md px-4 sm:px-6 py-3.5 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <a href="/" className="text-[color:var(--l-cream-3)] hover:text-[color:var(--l-cream)] transition-colors"><ArrowLeft className="w-5 h-5" /></a>
          <div className="w-8 h-8 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
            <span className="l-display text-[color:var(--l-bg)] text-xs">b</span>
          </div>
          <div>
            <h1 className="l-display text-[15px] flex items-center gap-2">
              {hotel.name}
              {hotel.verification_status === 'verified' && (
                <span className="text-[10px] text-[color:var(--l-bg)] bg-[color:var(--l-cream)]/90 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <ShieldCheck className="w-2.5 h-2.5" /> Verified
                </span>
              )}
            </h1>
            <p className="text-[11px] text-[color:var(--l-cream-3)]">{hotel.city}, {hotel.country}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Cart Icon Button */}
          <CartIconButton onClick={toggleCart} />
          
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" data-testid="booking-trip-summary-open-button">
                  <Receipt className="w-4 h-4 mr-1" /> Summary
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-2xl">
                <TripSummary bookingState={bookingState} hotel={hotel} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Split Layout - Emergent Experience (Chat Left + Live Preview Right) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Chat Area */}
        <div className="flex-1 lg:flex-[0.45] xl:flex-[0.4] flex flex-col border-r border-[color:var(--l-line)] bg-[color:var(--l-bg)]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-4 py-6">
            <div className="max-w-3xl mx-auto space-y-4">
              {/* Welcome */}
              {messages.length === 0 && (
                <div className="text-center py-12 lobby-reveal">
                  <div className="w-14 h-14 rounded-full bg-[color:var(--l-copper)]/15 border border-[color:var(--l-copper)]/30 flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-6 h-6 text-[color:var(--l-copper)]" />
                  </div>
                  <h2 className="l-display text-3xl sm:text-4xl mb-3">
                    Welcome to <span className="text-[color:var(--l-copper)]">{hotel.name}</span>.
                  </h2>
                  <p className="text-[color:var(--l-cream-2)] mb-8 max-w-md mx-auto leading-relaxed text-[15px]">
                    I&rsquo;m Bitsy — your concierge here. Tell me what you need in plain words, and I&rsquo;ll take care of the rest.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {['A quiet room this weekend', 'What rooms do you have?', 'Looking for a family suite'].map((q, i) => (
                      <button
                        key={i}
                        className="l-btn-ghost px-4 py-2 text-[13px]"
                        onClick={() => sendMessage(q)}
                        data-testid={`booking-quick-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => {
                // Gallery images - show in chat AND trigger preview update
                if (msg.role === 'gallery') {
                  return (
                    <div key={i} className="message-enter">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-sm text-muted-foreground pt-1">Here's a look at our property:</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 ml-11 max-w-sm">
                        {msg.images.map((imgUrl, idx) => (
                          <div 
                            key={idx} 
                            className="relative aspect-video rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all group"
                            onClick={() => setPreviewImage(imgUrl)}
                          >
                            <img 
                              src={imgUrl} 
                              alt={`Hotel view ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                              <span className="text-white text-xs opacity-0 group-hover:opacity-100">👁️ Preview</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                
                // Show conversational messages
                return <ChatBubble 
                  key={i} 
                  role={msg.role} 
                  content={msg.content}
                  isPersonalized={msg.isPersonalized}
                  personalizationSources={msg.personalizationSources}
                />;
              })}

              {loading && <TypingIndicator />}

            </div>
          </div>

          {/* Input Area - Keep at bottom of chat */}
          {!booking && (
            <div className="sticky bottom-0 z-20 bg-[color:var(--l-bg)]/90 backdrop-blur-md border-t border-[color:var(--l-line)]">
              {/* Trip Summary Chips */}
              {(bookingState.check_in || bookingState.room_type) && (
                <div className="px-4 pt-2 pb-1 flex gap-1.5 flex-wrap text-xs">
                  {bookingState.check_in && (
                    <Badge variant="secondary" className="text-[10px]">
                      <Calendar className="w-2.5 h-2.5 mr-1" />{bookingState.check_in} → {bookingState.check_out}
                    </Badge>
                  )}
                  {bookingState.guests && (
                    <Badge variant="secondary" className="text-[10px]">
                      <Users className="w-2.5 h-2.5 mr-1" />{bookingState.guests} guest{bookingState.guests > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {bookingState.room_type && (
                    <Badge variant="secondary" className="text-[10px]">{bookingState.room_type}</Badge>
                  )}
                </div>
              )}
              <div className="px-4 py-3 flex items-center gap-2">
                <Button variant="outline" size="icon"
                        className={`h-12 w-12 rounded-xl flex-shrink-0 ${isListening ? 'voice-active bg-primary text-primary-foreground' : ''}`}
                        onClick={toggleVoice}
                        data-testid="booking-chat-voice-button">
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
                <Input ref={inputRef}
                       value={input}
                       onChange={(e) => setInput(e.target.value)}
                       onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
                       placeholder={isListening ? 'Listening...' : 'Type your message...'}
                       className="h-12 rounded-2xl text-base"
                       data-testid="booking-chat-input-textarea" />
                <Button size="icon" className="h-12 w-12 rounded-xl flex-shrink-0"
                        onClick={() => sendMessage(input)}
                        disabled={!input.trim() || loading}
                        data-testid="booking-chat-send-button">
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Live Preview Pane (Updates based on chat) */}
        <aside className="hidden lg:flex lg:w-[55%] xl:w-[60%] border-l border-[color:var(--l-line)] bg-[color:var(--l-bg-2)] flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Room Photo Gallery - Interactive room type browser */}
            {roomTypes.length > 0 ? (
              <RoomPhotoGallery
                roomTypes={roomTypes}
                selectedRoomType={bookingState.room_type}
                hotelImages={hotel.image_url ? [hotel.image_url] : []}
                onPreviewChange={(img) => setPreviewImage(img)}
                hotelId={hotelId}
                onBookFromViewer={(roomName, roomDetails, availability) => sendMessage(
                  `I'd like to book the ${roomName} please`,
                  {
                    source: '3d_viewer',
                    room_viewed: roomName,
                    room_details: roomDetails || {},
                    availability: availability || {},
                  }
                )}
              />
            ) : (previewImage || hotel.image_url) && (
              <div className="relative rounded-2xl overflow-hidden aspect-[16/10] group" data-testid="preview-hero-image">
                <img 
                  src={previewImage || hotel.image_url}
                  alt="Preview" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h2 className="text-2xl font-bold text-white heading-font mb-1">
                    {bookingState.room_type || hotel.name}
                  </h2>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{hotel.city}, {hotel.country}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Hotel Info Card - Show initially */}
            {!bookingState.room_id && (
              <div className="bg-card/60 backdrop-blur-sm border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Property Type</span>
                  <Badge variant="outline">{hotel.category || 'Hotel'}</Badge>
                </div>
                {hotel.description && (
                  <div>
                    <span className="text-sm text-muted-foreground block mb-1">About</span>
                    <p className="text-sm leading-relaxed">{hotel.description}</p>
                  </div>
                )}
                {allRooms.length > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm text-muted-foreground">Starting from</span>
                    <p className="text-2xl font-bold heading-font mono">
                      ${Math.min(...allRooms.map(r => r.price_per_night))}
                      <span className="text-sm font-normal text-muted-foreground">/night</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Cancellation Policy - ALWAYS SHOW (transparency before booking) */}
            {!bookingState.room_id && hotel && (
              <div data-testid="cancellation-policy-preview">
                <CancellationPolicyCard 
                  hotelId={hotelId}
                  hotel={hotel}
                  guestEmail={bookingState.guest_email || null}
                  compact={false}
                />
              </div>
            )}

            {/* Trip Summary - Show when booking is in progress */}
            {(bookingState.check_in || bookingState.room_type) && (
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  Your Booking Details
                </h3>
                <TripSummary bookingState={bookingState} hotel={hotel} pricingBreakdown={pricingBreakdown} />
              </div>
            )}

            {/* AI-Suggested Rooms - Show when Bitsy suggests them */}
            {availableRooms.length > 0 && !bookingState.room_id && (
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span>Recommended for You</span>
                  <Badge variant="secondary" className="text-xs">{availableRooms.length}</Badge>
                </h3>
                <div className="space-y-3">
                  {availableRooms.map((room) => {
                    const matchedRT = roomTypes.find(rt => rt.name === room.room_type || rt.name === room.name);
                    const roomImage = matchedRT?.hero_image || matchedRT?.images?.[0];
                    return (
                    <div
                      key={room.id}
                      className="w-full bg-card/80 border border-border rounded-xl overflow-hidden transition-all hover:border-primary/30 hover:shadow-md"
                      data-testid={`preview-room-${room.id}`}
                    >
                      {/* Room Image */}
                      {roomImage && (
                        <div className="h-32 overflow-hidden relative group">
                          <img src={roomImage} alt={room.room_type} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        </div>
                      )}
                      <div className="p-3">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="font-semibold text-sm">{room.room_type}</h4>
                        <span className="font-bold mono text-sm">${room.price_per_night}/nt</span>
                      </div>
                      {room.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{room.description}</p>
                      )}
                      <div className="flex gap-2 mt-2 flex-wrap items-center">
                        {room.capacity && (
                          <Badge variant="outline" className="text-[10px]">
                            <Users className="w-2.5 h-2.5 mr-1" />
                            {room.capacity} guests
                          </Badge>
                        )}
                        <div className="flex gap-2 ml-auto">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRoomSelect(room)}
                            className="h-7 text-xs"
                            data-testid={`select-room-${room.id}-btn`}
                          >
                            Select
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Add to cart with current booking state
                              if (bookingState.check_in && bookingState.check_out) {
                                addToCart({
                                  hotel_id: hotelId,
                                  room_id: room.id,
                                  room_type: room.room_type,
                                  check_in: bookingState.check_in,
                                  check_out: bookingState.check_out,
                                  guests: bookingState.guests || 1,
                                  price_per_night: room.price_per_night
                                });
                                toast.success(`${room.room_type} added to cart!`);
                              } else {
                                toast.error('Please set check-in and check-out dates first');
                              }
                            }}
                            className="h-7 text-xs bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
                            data-testid={`add-to-cart-room-${room.id}-btn`}
                          >
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            Add to Cart
                          </Button>
                        </div>
                      </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Payment Card - Show when ready for payment (behind ID verification gate) */}
            {showPayment && !booking && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <IdVerifyGate
                  email={bookingState.guest_email}
                  bookingName={bookingState.guest_name}
                  checkInIso={bookingState.check_in}
                  hotelId={hotelId}
                  hotelMinAge={hotel?.ai_terms?.guest_requirements?.min_age || 18}
                  walletAddress={guestWallet || null}
                >
                  <PaymentCard
                    bookingState={bookingState}
                    pricingInfo={pricingInfo}
                    hotel={hotel}
                    onPay={(method, chain) => handlePay(method, chain)}
                    onPayAtProperty={() => handlePay('pay_at_property')}
                    onPayGasless={() => setShowGaslessCheckout(true)}
                    onAddToCart={handleAddToCart}
                    loading={payLoading}
                  />
                </IdVerifyGate>
              </div>
            )}

            {/* Booking Confirmation - Show after successful booking */}
            {booking && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <BookingConfirmation 
                  booking={booking} 
                  pricingInfo={pricingInfo} 
                  payment={payment} 
                  hotel={hotel} 
                  nftMintingInfo={nftMintingInfo} 
                />
              </div>
            )}

            {/* Crypto Badge */}
            {hotel.accepts_crypto && !booking && (
              <div className="bg-[hsl(var(--crypto))]/10 border border-[hsl(var(--crypto))]/30 rounded-xl p-3 flex items-center gap-3">
                <Wallet className="w-5 h-5 text-[hsl(var(--crypto))]" />
                <div>
                  <p className="text-sm font-semibold">Crypto Payments Accepted</p>
                  <p className="text-xs text-muted-foreground">Base, Ethereum, Polygon, Bitcoin, Solana</p>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
      
      {/* Cart Drawer */}
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
      
      {/* Preference Collection Modal (After First Booking) */}
      <PreferenceCollectionModal
        open={showPreferenceModal}
        onOpenChange={setShowPreferenceModal}
        guestId={guestId}
        onComplete={() => {
          toast.success('✨ Great! Bitsy will use your preferences for future recommendations.');
        }}
      />
    </div>
  );
}
